import { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { useJourney } from '../contexts/JourneyContext.js';
import styles from '../Styles.jsx';
import Contentitem from './ContentItem';
import ContentItemsListCompleteButton from './ContentItemsListCompleteButton';
import FadeIn from './FadeIn';

export default function ContentItemsList(props) {
  // props include:
  // - contentItems, isShowingLargeText, showSingle, dontShowFinishedButton

  const [visibleItems, setVisibleItems] = useState(null);
  const [itemIdShowingCover, setItemIdShowingCover] = useState(null);
  const addItemTimeoutRef = useRef(null);

  const journey = useJourney();

  // Debug log when visibleItems changes
  useEffect(() => {
    //console.log('visibleItems changed:', visibleItems?.length || 0, 'items');
    //console.log('itemIdShowingCover:', itemIdShowingCover);
    if (visibleItems?.length) {
      //console.log('Items in order:', visibleItems.map(item => `${item.u_id} (pos: ${item.position})`));
    }
  }, [visibleItems, itemIdShowingCover]);

  useEffect(() => {
    const setStartingItem = () => {
      if (visibleItems == null && props.contentItems && props.contentItems.length > 0) {
        if (!(haveAllItemsBeenSeen())) {
          addContentItem();
        }
      }
    };

    addItemTimeoutRef.current = setTimeout(() => {
      setStartingItem();
    }, 1000);

    return () => {
      if (addItemTimeoutRef.current) {
        clearTimeout(addItemTimeoutRef.current);
      }
    };
  }, [visibleItems, props.contentItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const getNextItem = () => {
    let result = null;

    for (let i=0; i<props.contentItems.length && result == null; i++) {
      let nextItem = props.contentItems[i];
      if (hasItemNotBeenPlayed(nextItem) && canItemBePlayed(nextItem)) {
        result = nextItem;
      }
    }

    return result;
  };

  const addContentItem = () => {
    let nextItem = getNextItem();
    if (!!nextItem) {
      // Create a copy of the item instead of mutating the original
      const itemCopy = { ...nextItem };
      
      // Use functional state update to ensure immutability
      setVisibleItems(prev => {
        const newItems = prev == null ? [itemCopy] : [itemCopy, ...prev];
        return newItems;
      });
      setItemIdShowingCover(itemCopy.u_id);
      
      //console.log('Added item:', itemCopy.u_id, 'showing cover:', itemCopy.u_id);
    } else {
      //console.log('No more items to add');
    }
  };

  const canItemBePlayed = (contentItem) => {
    let result = true;
    
    if (hasAbilityMatch(contentItem) === false) {
      result = false;
    }
    
    if (journey.isFirstHunt()) {
      if (journey.doesItemHaveTag(contentItem, "NotFirstTime")) {
        return false;
      }
    } else {
      if (journey.doesItemHaveTag(contentItem, "FirstOnly")) {
        return false;
      }
    }
    return result;
  };
  
  const hasAbilityMatch = (contentItem) => {
    let result = false;

    let itemHasHearingTag = journey.doesItemHaveTag(contentItem, "PoorHearing");
    let itemHasMobilityTag = journey.doesItemHaveTag(contentItem, "PoorMobility");
    let itemHasVisionTag = journey.doesItemHaveTag(contentItem, "PoorVision");

    // if this has no ability tags, then there's an ability match
    if (itemHasHearingTag === false && itemHasMobilityTag === false && itemHasVisionTag === false) {
      result = true;
    } else {
      // "Big Text", "Spoken", "Blind" "Sore", "Cane", "Wheelchair" "Captions"

      // if this has ability tags, and they match with the passed user ability flags, then also a match
      if (itemHasHearingTag === true) {
        if (props.isShowingClosedCaptioning === true) {
          result = true;
        }
      }

      if (itemHasMobilityTag === true) {
        if (props.accessibility.indexOf("Sore") > -1 || props.accessibility.indexOf("Cane") > -1 || props.accessibility.indexOf("Wheelchair") > -1) {
          result = true;
        }
      }

      if (itemHasVisionTag === true) {
        if (props.accessibility.indexOf("Big Text") > -1 || props.accessibility.indexOf("Spoken") > -1 || props.accessibility.indexOf("Blind") > -1) {
          result = true;
        }
      }
    }
    return result;
  };

  const hasItemBeenPlayed = (contentitem) => {
    let matchFound = false;
    if (!!visibleItems) {
      for (let i=0; i<visibleItems.length && !(matchFound) ; i++) {
        let nextKey = visibleItems[i].u_id;
        if (nextKey === contentitem.u_id) {
          matchFound = true;
        }
      }
    }
    return matchFound;
  };

  const hasItemNotBeenPlayed = (contentitem) => {
    return !(hasItemBeenPlayed(contentitem));
  };

  const openedItem = (itemkey) => {
    //console.log('Item opened:', itemkey, 'clearing cover state');
    setItemIdShowingCover(null);
    
    // Add delay before adding next item to ensure proper state transition
    setTimeout(() => {
      //console.log('Adding next item after delay');
      addContentItem();
    }, 100);
  };

  const haveAllItemsBeenSeen = () => {
    let result = true;
    for (let i=0; i<props.contentItems.length && result; i++) {
      let nextItem = props.contentItems[i];
      if (isItemShowingCover(nextItem) || hasItemNotBeenPlayed(nextItem)) {
        if (canItemBePlayed(nextItem)) {
          result = false;
        }
      }
    }
    return result;
  };

  const isItemShowingCover = (contentItem) => {
    let result = false;
    
    if (contentItem.u_id === itemIdShowingCover) {
      result = true;
    }
    
    //console.log(`Item ${contentItem.u_id} showing cover:`, result, 'current itemIdShowingCover:', itemIdShowingCover);
    return result;
  };

  const tappedCompletionButton = () => {
    props.completionButtonTapped();
  };

  const canShowFinishedButton = () => {
    let result = false;
    if (props.dontShowFinishedButton === true) {
      return false;
    }
    if (haveAllItemsBeenSeen()) {
      result = true;
    }   
    return result;
  };

  // Render function content
  if (!!(props.showSingle)) {
    if (canShowFinishedButton()) {
      return (
        <FadeIn>
          <View>
            <ContentItemsListCompleteButton
              visible={true}
              label={props.completionButtonLabel}
              coverStyle={styles.startVoyageCoverDark}
              tappedButton={() => tappedCompletionButton()}
              skipIcon={props.completionButtonSkipIcon}
            />
          </View>
        </FadeIn>
      )
    } else {
      let currentItem = visibleItems[0];
      return (
        <FadeIn>
          <Contentitem
            contentitem={currentItem}
            showingCover={isItemShowingCover(currentItem)}
            coverImage={props.coverImage}
            defaultCoverText="Tap for More"
            totalItems={visibleItems.length}
            openedItem={itemkey => openedItem(itemkey)}
          />
        </FadeIn>
      )
    }
  } else {
    return (
      <View>
        <ContentItemsListCompleteButton
          visible={canShowFinishedButton()}
          label={props.completionButtonLabel}
          coverStyle={styles.startVoyageCoverDark}
          tappedButton={() => tappedCompletionButton()}
          skipIcon={props.completionButtonSkipIcon}
        />
        {
          !!visibleItems && visibleItems.map((prop, key) => {
            return (
              <Contentitem
                contentitem={prop}
                showingCover={isItemShowingCover(prop)}
                coverImage={props.coverImage}
                defaultCoverText="Tap for More"
                key={key}
                totalItems={visibleItems.length}
                openedItem={itemkey => openedItem(itemkey)}
              />
            )
          })
        }
      </View>
    )
  }
}